const PERSONALIZATION_DEBOUNCE_DURATION = 350;
const PERSONALIZATION_MIN_LOADING_DURATION = 1000;
const PERSONALIZATION_ERROR_MAP = {
  '"Personalization exceeds maximum length"': 'errorSewingAreaExceeded',
  '"Sewing area exceeded, too many characters."': 'errorSewingAreaExceeded',
  '"Personalization has unsupported character"': 'errorInvalidCharacters',
};
const ALLOWED_CHARACTERS_REGEX = {
  Standard: `[a-zA-Z0-9!@#$%&+,.?_"' \\-]+`,
  Extended: `[a-zA-Z0-9!@#$*&()_,./?;:'"+ =\\-]+`,
};
const DEFAULT_IMAGE_CLASS = '.p13n-image__fallback';
const COLOR_IMAGE_CLASS_PREFIX = '.p13n-image__option-color--';
const ALL_COLOR_IMAGE_SELECTOR = '.p13n-image';

const PROCESS_TYPE = {
  EMBROIDERY: 'embroidery',
  ENGRAVING: 'engraving',
};

class PersonalizationSettings extends HTMLElement {
  constructor() {
    super();
  }

  // PubSub events
  onVariantChangeUnsubscriber = undefined;
  onOptionValueSelectionChangeUnsubscriber = undefined;
  onCartUpdateUnsubscriber = undefined;

  // Bound event handlers
  boundAddPersonalizationClickHandler = undefined;
  boundOptionChangeHandler = undefined;

  // Personalization elements
  optionElement = undefined;
  closeButtonElement = undefined;
  textInputElement = undefined;
  personalizedImageElement = undefined;
  invalidCharacterErrorElement = undefined;
  personalizationBadge = undefined;
  personalizeStatusElement = undefined;
  invalidWordErrorElement = undefined;
  customErrorElement = undefined;
  selectedVariantOptionsElement = undefined;
  styleRadioElements = undefined;
  fontRadioElements = undefined;
  colorRadioElements = undefined;
  previewStatusElement = undefined;
  formElement = undefined;
  characterCounterElement = undefined;
  submitButtonElement = undefined;
  cartFormElement = undefined;
  cartSubmitButtonElement = undefined;
  dialogElement = undefined;
  priceAmountElement = undefined;
  priceCompareElement = undefined;
  priceCurrentElement = undefined;

  // Personalization state
  debouncedTextChange = undefined;
  variantSku = undefined;
  threadColor = undefined;
  transactionId = undefined;
  variantsData = [];
  replaceLineItemId = undefined;

  // AbortControllers for fetch requests
  profanityCheckAbortController = undefined;
  fetchPersonalizationAbortController = undefined;
  cart = undefined;

  // Loading state
  loadingRequestCount = 0;
  loadingStartedAt = undefined;
  loadingTimer = undefined;

  // Product nested line item data - not sku but id!
  personalizationNestedVariantId = undefined;
  currentVariantId = undefined;
  personalizationAddonPrice = undefined;
  reservePersonalizationAddonPrice = undefined;

  // Add Personalization Button
  triggerModalButton = undefined;

  // Process type
  processType = undefined;

  connectedCallback() {
    this.dialogElement = this.querySelector('dialog');

    // Listen for add personalization button clicks
    this.boundAddPersonalizationClickHandler = this.handleAddPersonalizationClick.bind(this);
    document.addEventListener('click', this.boundAddPersonalizationClickHandler);

    this.dialogElement.addEventListener('toggle', () => {
      if (this.dialogElement.open) {
        // Clear any existing selections in case the browser restored them from cache
        this.styleRadioElements.forEach((radio) => (radio.checked = false));
        this.fontRadioElements.forEach((radio) => (radio.checked = false));
        this.colorRadioElements.forEach((radio) => (radio.checked = false));
        this.textInputElement.value = '';
      } else {
        // Clear replacement flag if modal is closed without completing
        this.replaceLineItemId = undefined;
        this.clearAndResetPersonalization();
      }
    });

    // Initialize references to personalization elements
    this.textInputElement = this.querySelector('#p13n-text-input');
    this.personalizedImageElement = this.querySelector('#p13n-personalized-image');
    this.personalizedImageElement.addEventListener('load', () => {
      const translateX = this.personalizedImageElement.width / 2;
      const translateY = this.personalizedImageElement.height / 2;
      this.personalizedImageElement.parentElement.style.transform = `translate(-${translateX}px, -${translateY}px)`;
    });
    this.personalizationBadge = this.querySelector('[data-personalize-badge]');
    this.personalizeStatusElement = this.querySelector('[data-personalize-status]');
    this.invalidCharacterErrorElement = this.querySelector('#p13n-invalid-character-error');
    this.invalidWordErrorElement = this.querySelector('#p13n-invalid-word-error');
    this.customErrorElement = this.querySelector('#p13n-custom-error');
    this.selectedVariantOptionsElement = this.querySelector('#p13n-selected-variant-options');
    this.styleRadioElements = this.querySelectorAll("input[name='p13n-styles']");
    this.fontRadioElements = this.querySelectorAll("input[name='p13n-fonts']");
    this.colorRadioElements = this.querySelectorAll("input[name='p13n-colors']");
    this.previewStatusElement = this.querySelector('#p13n-preview-status');
    this.formElement = this.querySelector('#p13n-form');
    this.characterCounterElement = this.querySelector('#p13n-character-counter');
    this.submitButtonElement = this.querySelector('#p13n-submit-button');
    this.closeButtonElement = this.querySelector('#p13n-close');
    this.closeButtonElement.addEventListener('click', this.handleCloseDialog.bind(this));
    this.priceAmountElement = this.querySelector('.p13n-price-amount');
    this.priceCompareElement = this.querySelector('#p13n-price-compare');
    this.priceCurrentElement = this.querySelector('#p13n-price-current');
    this.cartFormElement = document.querySelector('form[action*="/cart/add"][data-type="add-to-cart-form"]');
    this.cartSubmitButtonElement = this.cartFormElement?.querySelector('button[type="submit"]') ?? null;
    this.variantsData = this.getVariantsData();
    this.variantColorOptions = this.dataset.variantColorOptions || '';
    this.processType = this.dataset.processType || '';

    // Get the personalization product SKU and current variant ID from the data attribute
    this.personalizationNestedVariantId = this.submitButtonElement.dataset.personalizationProduct || '';
    this.currentVariantId = this.submitButtonElement.dataset.currentVariant || '';
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    this.personalizationAddonPrice = this.submitButtonElement.dataset.personalizationAddonPrice || '';
    this.reservePersonalizationAddonPrice = this.submitButtonElement.dataset.reservePersonalizationAddonPrice || '';

    this.triggerModalButton = document.getElementById('open-personalization-modal');
    if (this.triggerModalButton) {
      this.triggerModalButton.addEventListener('click', () => {
        this.openPersonalizationModal();
      });
    }

    // Subscribe to pubsub events to listen for variant changes
    this.subscribeToPubSubEvents();

    // Listen for changes to style, font, and color inputs
    this.boundOptionChangeHandler = this.handleOptionChange.bind(this);
    document.addEventListener('change', this.boundOptionChangeHandler);

    // Listen for changes to text input with debounce to check for allowed words and to update the personalization image
    this.debouncedTextChange = debounce((event) => {
      this.handlePersonalizationChange();
    }, PERSONALIZATION_DEBOUNCE_DURATION);
    this.textInputElement.addEventListener('input', this.debouncedTextChange.bind(this));

    // Listen for changes to text to update any error messaging
    this.textInputElement.addEventListener('input', async () => {
      this.updateTextCase();
      const isInvalid = this.textInputElement.validity.patternMismatch;
      this.invalidCharacterErrorElement.classList.toggle('hide', !isInvalid);
      this.customErrorElement.classList.add('hide');
      this.updateCharacterCount();
    });

    // Enable or disable submit button based on input validity
    this.formElement.addEventListener('input', () => {
      this.submitButtonElement.disabled = !this.formElement.checkValidity();
    });

    // Handle form submission to save personalization settings to hidden fields
    this.formElement.addEventListener('submit', (event) => {
      event.preventDefault();
      if (this.classList.contains('loading')) return; // Prevent submission while loading since we need the latest transaction ID
      this.submitButtonElement.disabled = true;
      try {
        if (!this.personalizationNestedVariantId) {
          throw new Error('No personalization product selected');
        }
        const style = this.getStyle();
        const font = this.getFont();
        const color = this.getColor();
        const color_name = this.getColorName();
        const text = this.getText();
        const process = this.processType;
        const stitchHeight = this.dataset.stitchHeight;
        const threadColor = this.threadColor;
        if (!style || !font || !color || !color_name || !text || !process || !this.transactionId) {
          console.error('Invalid parameters for personalization');
          this.submitButtonElement.disabled = false;
          return;
        }
        if (process === PROCESS_TYPE.EMBROIDERY && !threadColor) {
          console.error('Thread color is required for embroidery');
          this.submitButtonElement.disabled = false;
          return;
        }
        this.handlePersonalizationAddToCart(style, font, color, color_name, text, process, stitchHeight, threadColor);
      } catch (error) {
        console.error('Error submitting personalization form:', error);
        this.submitButtonElement.disabled = false;
      }
    });
  }

  async handlePersonalizationAddToCart(style, font, color, color_name, text, process, stitchHeight, threadColor) {
    const items = [
      {
        id: this.currentVariantId,
        quantity: 1,
        properties: {
          _nested_transaction_id: this.transactionId,
        },
      },
      {
        id: this.personalizationNestedVariantId,
        quantity: 1,
        parent_id: this.currentVariantId,
        properties: {
          _personalization_style: style,
          _personalization_font: font,
          _personalization_color: color,
          _personalization_color_name: color_name,
          _personalization_text: text,
          _personalization_process: process,
          _personalization_stitch_height: stitchHeight || '',
          _personalization_thread_color: threadColor || '',
          _personalization_transaction_id: this.transactionId,
        },
      },
    ];

    try {
      const sections = this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [];

      if (this.replaceLineItemId) {
        const removeResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: this.replaceLineItemId, quantity: 0 }),
        });
        if (!removeResponse.ok) throw new Error('Failed to remove old cart item');
        this.replaceLineItemId = undefined;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const parsedState = await response.json();
      parsedState.key = parsedState.items?.[0]?.key;

      if (this.cart) {
        this.cart.renderContents(parsedState);
      }
      this.handleCartUpdate();
    } catch (error) {
      this.dialogElement.close();
      // eslint-disable-next-line no-console
      console.error('Error adding to cart', error);
      const toastEL = document.getElementById('toast-message');
      const errorTitle = window.cartStrings?.error_title || 'Error';
      const errorMessage =
        window.cartStrings?.error || 'There was an error while updating your cart. Please try again.';
      if (toastEL) {
        toastEL.open(errorTitle, errorMessage);
      } else {
        const errorWrapper = document.querySelector('.product-form__error-message-wrapper');
        const errorEl = document.querySelector('.product-form__error-message');
        if (errorWrapper && errorEl) {
          errorEl.textContent = errorMessage;
          errorWrapper.removeAttribute('hidden');
        }
      }

      this.submitButtonElement.disabled = false;
    }
  }

  disconnectedCallback() {
    // Unsubscribe from pubsub events
    this.onVariantChangeUnsubscriber();
    this.onCartUpdateUnsubscriber();

    // Remove document event listeners
    if (this.boundAddPersonalizationClickHandler) {
      document.removeEventListener('click', this.boundAddPersonalizationClickHandler);
    }
    if (this.boundOptionChangeHandler) {
      document.removeEventListener('change', this.boundOptionChangeHandler);
    }
  }

  updateTextCase() {
    if (this.textInputElement.dataset.textCase?.toLowerCase() === 'uppercase') {
      this.textInputElement.value = this.textInputElement.value.toUpperCase();
    } else if (this.textInputElement.dataset.textCase?.toLowerCase() === 'lowercase') {
      this.textInputElement.value = this.textInputElement.value.toLowerCase();
    }
  }

  // Handle add personalization button click
  handleAddPersonalizationClick(event) {
    const button = event.target.closest('[data-add-personalization-button]');
    if (!button) return;

    const lineItemId = button.dataset.lineItemId;
    if (lineItemId) {
      // Store the line item key and its existing nested transaction ID to remove later
      this.replaceLineItemId = lineItemId;

      // Open personalization modal
      this.openPersonalizationModal();
    }
  }

  // Subscribe to pubsub events to listen for variant changes
  subscribeToPubSubEvents() {
    this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
    this.onCartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate.bind(this));
  }

  // Handle cart update - clean up personalization state
  handleCartUpdate() {
    this.clearAndResetPersonalization();
  }

  updateFormProperty(key, value) {
    let propertyElement = this.cartFormElement.querySelector(`input[name="properties[${key}]"]`);
    if (!propertyElement) {
      propertyElement = document.createElement('input');
      propertyElement.type = 'hidden';
      propertyElement.name = `properties[${key}]`;
      this.cartFormElement.appendChild(propertyElement);
    }
    propertyElement.value = value;
  }

  removeFormProperty(key) {
    this.cartFormElement.querySelector(`input[name="properties[${key}]"]`)?.remove();
  }

  // Reset personalization option back to 'No Personalization'
  resetPersonalization() {
    this.fontRadioElements?.forEach((radio) => (radio.disabled = true));
    this.colorRadioElements?.forEach((radio) => (radio.disabled = true));
    if (this.textInputElement) {
      this.textInputElement.disabled = true;
    }
    if (this.submitButtonElement) {
      this.submitButtonElement.disabled = true;
    }
    if (this.personalizedImageElement) {
      this.personalizedImageElement.style.display = 'none';
    }
  }

  // Open or close the personalization dialog based on variant change
  handleVariantChange(event) {
    if (!event.data?.variant) return;
    const variant = event.data.variant;
    this.variantSku = variant.sku;
    this.updateVariantTitle(variant);
    this.updateVariantPrice(variant);
    this.updateDisplayImage(variant.options);
    this.updateTriggerButtonState(variant);
    this.dialogElement.close();
    this.submitButtonElement.dataset.currentVariant = event.data.variant.id;
    this.currentVariantId = event.data.variant.id;
  }

  updateTriggerButtonState(variant) {
    if (!this.triggerModalButton) return;
    const isPreorder = this.triggerModalButton.dataset.preorderTag === 'true';
    const isAvailable =
      variant?.available === true ||
      (isPreorder && variant.inventory_management && variant.inventory_policy == 'continue');
    this.triggerModalButton.disabled = !isAvailable;
  }

  openPersonalizationModal() {
    this.dialogElement.showModal();
    this.dialogElement.offsetHeight;
    this.dialogElement.classList.add('slide-up');
  }

  // Remove the personalization options after they have been added to the cart and close the dialog
  clearAndResetPersonalization() {
    this.removeFormProperty('_personalization_style');
    this.removeFormProperty('_personalization_font');
    this.removeFormProperty('_personalization_color');
    this.removeFormProperty('_personalization_process');
    this.removeFormProperty('_personalization_text');
    this.removeFormProperty('_personalization_stitch_height');
    this.removeFormProperty('_personalization_thread_color');
    this.removeFormProperty('_personalization_transaction_id');
    this.resetPersonalization();
    this.dialogElement.close();
  }

  updateDisplayImage(variantOptions) {
    // determine current color
    const normalizedVariantColorOptions = this.variantColorOptions.split(',');
    let currentColor = undefined;
    for (const option of variantOptions) {
      const newOption = option.toLowerCase().replaceAll(' ', '-');
      if (normalizedVariantColorOptions.includes(newOption)) {
        currentColor = newOption;
        break;
      }
    }

    // update display image based on current color
    const currentColorImageClass = `${COLOR_IMAGE_CLASS_PREFIX}${currentColor}`;
    const allColorImageElements = this.querySelectorAll(`${ALL_COLOR_IMAGE_SELECTOR}`);
    allColorImageElements.forEach((image) => {
      image.style.display = 'none';
    });
    const currentColorImageElement = this.querySelector(currentColorImageClass);
    if (currentColorImageElement) {
      currentColorImageElement.style.display = 'block';
    } else {
      const defaultImageElement = this.querySelector(`${DEFAULT_IMAGE_CLASS}`);
      if (defaultImageElement) {
        defaultImageElement.style.display = 'block';
      }
    }
  }

  // Update the variant title in the modal
  updateVariantTitle(variant) {
    if (!variant) return;

    // Build a list of options
    const variantTitle = variant.options.join(' • ');

    // Update the modal element
    this.selectedVariantOptionsElement.innerText = variantTitle;
  }

  // Update the variant price in the modal
  updateVariantPrice(variant) {
    if (!variant || !this.priceAmountElement) return;

    const isReserveMember = this.dataset.isReserveMember === 'true';
    const fullPrice = this.getFullPersonalizationPrice(variant);
    const hasFullPrice = Number.isFinite(fullPrice);

    let currentPrice, compareAtFormatted;

    if (isReserveMember && hasFullPrice && this.processType !== 'engraving') {
      // Reserve: pays only variant price; full price (with addon) is shown crossed out
      currentPrice = formatMoneyWithCurrency(variant.price);
      compareAtFormatted = formatMoneyWithCurrency(fullPrice);
    } else {
      // Non-reserve: pays full price (variant + addon)
      currentPrice = formatMoneyWithCurrency(hasFullPrice ? fullPrice : variant.price);
      compareAtFormatted = null;
    }

    this.priceCurrentElement.textContent = currentPrice;

    if (compareAtFormatted) {
      this.priceCompareElement.textContent = compareAtFormatted;
      this.priceCompareElement.hidden = false;
      this.updateVariantPriceAccessibility({ compareAtPrice: compareAtFormatted, currentPrice });
      return;
    }

    this.priceCompareElement.textContent = '';
    this.priceCompareElement.hidden = true;
    this.updateVariantPriceAccessibility({ currentPrice });
  }

  getFullPersonalizationPrice(variant) {
    const addonPrice = Number(this.personalizationAddonPrice);
    if (!Number.isFinite(addonPrice)) return null;
    return variant.price + addonPrice;
  }

  updateVariantPriceAccessibility({ compareAtPrice = null, currentPrice }) {
    if (!this.priceAmountElement || !currentPrice) return;
    const regularPriceLabel = this.priceAmountElement.dataset.labelRegularPrice || 'Regular price';
    const salePriceLabel = this.priceAmountElement.dataset.labelSalePrice || 'Reserve member price';

    if (compareAtPrice) {
      this.priceAmountElement.setAttribute(
        'aria-label',
        `${regularPriceLabel}: ${compareAtPrice}. ${salePriceLabel}: ${currentPrice}.`
      );
      return;
    }

    this.priceAmountElement.setAttribute('aria-label', `${regularPriceLabel}: ${currentPrice}.`);
  }

  getVariantsData() {
    const variantsDataJSON = document.querySelector('[data-variants-json]')?.innerHTML;
    if (!variantsDataJSON) return [];

    try {
      return JSON.parse(variantsDataJSON);
    } catch (error) {
      return [];
    }
  }

  getReserveDiscountedPrice(variant) {
    if (this.dataset.isReserveMember !== 'true') return null;
    return variant.price;
  }

  // Update the character count in the modal
  updateCharacterCount() {
    const currentLength = this.textInputElement.value.length;
    const maxLength = this.textInputElement.getAttribute('maxlength');
    let remaining = maxLength - currentLength;
    if (maxLength === null || remaining < 0) {
      this.characterCounterElement.innerText = '';
      return;
    }
    this.characterCounterElement.innerText = remaining;
  }

  getText() {
    const text = this.textInputElement.value;
    return text;
  }

  getStyle() {
    return this.getRadioValue(this.styleRadioElements);
  }

  getFont() {
    return this.getRadioValue(this.fontRadioElements);
  }

  getColor() {
    return this.getRadioValue(this.colorRadioElements);
  }

  getColorName() {
    const selectedColors = this.querySelectorAll("input[name='p13n-colors']");
    const selectedColorElement = Array.from(selectedColors).find((radio) => radio.checked);
    return selectedColorElement?.dataset.threadColorName;
  }

  getRadioValue(radioElements) {
    return Array.from(radioElements).find((radio) => radio.checked)?.value;
  }

  handleOptionChange(event) {
    const target = event.target;
    const isStyle = target.matches('input[name="p13n-styles"]');
    const isFont = target.matches('input[name="p13n-fonts"]');
    const isColor = target.matches('input[name="p13n-colors"]');

    if (isStyle) {
      this.updateTextCase();
      this.fontRadioElements.forEach((radio) => {
        radio.disabled = false;
      });
    } else if (isFont) {
      this.colorRadioElements.forEach((radio) => {
        radio.disabled = false;
      });
      if (this.processType === PROCESS_TYPE.ENGRAVING) {
        this.colorRadioElements[0].checked = true;
        this.textInputElement.disabled = false;
        this.threadColor = '';
      }
    } else if (isColor) {
      this.textInputElement.disabled = false;
    }

    // Update allowed characters and character limit based on style selection
    if (isStyle) {
      const allowedChars = target.dataset.allowedCharacters;
      const maxChars = target.dataset.characterLimit;
      const textCase = target.dataset.textCase;
      this.textInputElement.value = ''; // Clear existing input
      this.submitButtonElement.disabled = true;
      this.textInputElement.setAttribute('pattern', ALLOWED_CHARACTERS_REGEX[allowedChars]);
      this.textInputElement.setAttribute('maxlength', maxChars);
      this.textInputElement.dataset.textCase = textCase;
      this.updateCharacterCount();
    }

    if (isColor) {
      this.threadColor = target.dataset.threadColor;
    }

    // Update personalization for any style, font, or color changes
    if (isStyle || isFont || isColor) {
      this.handlePersonalizationChange();
    }
  }

  async handlePersonalizationChange() {
    const font = this.getFont();
    const color = this.getColor();
    const text = this.getText();
    const shouldLoad = !!(font && color && (text?.trim() || '') !== '');
    if (shouldLoad) this.startLoading();
    try {
      const isApprovedText = await this.profanityCheck(text);
      this.textInputElement.setCustomValidity(isApprovedText ? '' : 'Invalid text');
      this.submitButtonElement.disabled = !this.formElement.checkValidity();
      this.invalidWordErrorElement.classList.toggle('hide', isApprovedText);
      if (isApprovedText && !this.textInputElement.validity.patternMismatch && text.trim() !== '') {
        this.updatePersonalization(text);
      } else {
        this.personalizedImageElement.style.display = 'none';
      }
    } finally {
      if (shouldLoad) this.stopLoading();
    }
  }

  startLoading() {
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer);
      this.loadingTimer = undefined;
    }
    if (this.loadingRequestCount === 0) {
      this.classList.add('loading');
      this.personalizationBadge?.setAttribute('aria-busy', 'true');
      if (this.personalizeStatusElement && this.personalizationBadge?.dataset.labelBusy) {
        this.personalizeStatusElement.textContent = this.personalizationBadge.dataset.labelBusy;
      }
      this.loadingStartedAt = performance.now();
    }
    this.loadingRequestCount += 1;
  }

  stopLoading() {
    if (this.loadingRequestCount === 0) return;
    this.loadingRequestCount -= 1;
    if (this.loadingRequestCount > 0) return;

    const elapsed = performance.now() - this.loadingStartedAt;
    const remaining = Math.max(0, PERSONALIZATION_MIN_LOADING_DURATION - elapsed);
    this.loadingTimer = setTimeout(() => {
      this.loadingTimer = undefined;
      this.classList.remove('loading');
      this.personalizationBadge?.removeAttribute('aria-busy');
      if (this.personalizeStatusElement) this.personalizeStatusElement.textContent = '';
    }, remaining);
  }

  async updatePersonalization(text) {
    const sku = this.submitButtonElement.dataset.personalizationProductSku || 'P13N-EMBR-APRL-60';
    const font = this.getFont();
    const color = this.getColor();
    if (sku && font && color) {
      try {
        const personalizationImageResponse = await this.fetchPersonalization(text, sku, font, color);
        if (personalizationImageResponse) {
          this.transactionId = personalizationImageResponse.transactionId;
          this.personalizedImageElement.src = URL.createObjectURL(personalizationImageResponse.blob);
          this.personalizedImageElement.style.display = 'block';
          this.previewStatusElement.innerText = this.previewStatusElement.dataset.previewUpdated + ' ' + text;
        }
      } finally {
        this.stopLoading();
      }
    }
  }

  async profanityCheck(text) {
    if (text.trim() === '') {
      return true;
    }
    if (this.profanityCheckAbortController) this.profanityCheckAbortController.abort();
    this.profanityCheckAbortController = new AbortController();
    return await fetch(this.dataset.validationUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text,
      }),
      signal: this.profanityCheckAbortController.signal,
    })
      .then((response) => response.json())
      .then((data) => {
        return data?.result == 'approved';
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error('Error validating text', error);
        return false;
      });
  }

  async fetchPersonalization(text, sku, font, color) {
    if (!text || text.trim() === '' || !sku || !font || !color) {
      console.error('Invalid parameters for personalization');
      return null;
    }
    if (this.fetchPersonalizationAbortController) this.fetchPersonalizationAbortController.abort();
    this.fetchPersonalizationAbortController = new AbortController();
    const encodedText = encodeURIComponent(text);
    const encodedSku = encodeURIComponent(sku);
    const encodedFont = encodeURIComponent(font);
    const encodedColor = encodeURIComponent(color.replace('#', 'A'));
    const height = !isNaN(Number(this.dataset.stitchHeight)) ? `&height1=${Number(this.dataset.stitchHeight)}` : '';
    const response = await fetch(
      `${this.dataset.dynamicAssetUrl}?sku=${encodedSku}&style=${encodedFont}${height}&pers1=${encodedText}&color1=${encodedColor}&sendpng=y`,
      {
        method: 'GET',
        headers: {
          Accept: 'image/png',
        },
        signal: this.fetchPersonalizationAbortController.signal,
      }
    ).catch((error) => {
      if (error.name !== 'AbortError') {
        console.error('Error fetching personalization image', error);
        this.customError();
      }
      return null;
    });
    if (!response) {
      return null;
    }
    if (!response.ok) {
      const errorText = response ? await response.text() : null;
      const errorMessageKey = PERSONALIZATION_ERROR_MAP[errorText];
      if (!errorMessageKey || !this.dataset[errorMessageKey]) {
        console.error(`Bad status code fetching personalization image: ${response?.status}`, errorText);
      }
      this.customError(this.dataset[errorMessageKey]);
      return null;
    }
    const transactionId = response.headers.get('Transaction');
    const blob = await response.blob();
    return {
      transactionId,
      blob,
    };
  }

  customError(errorMessage = null) {
    const message = errorMessage || this.customErrorElement.dataset.defaultText;
    this.customErrorElement.innerHTML = message;
    this.customErrorElement.classList.remove('hide');
    this.textInputElement.setCustomValidity(message);
    this.submitButtonElement.disabled = !this.formElement.checkValidity();
  }

  handleCloseDialog() {
    this.dialogElement.close();
  }
}

customElements.define('personalization-settings', PersonalizationSettings);
